import type { TUser } from '@modules/users/types/users.types';

import type { TApprovalUser, TApprovalUserEntry } from '../types';

/** One row in the function-user picker: a selected custom group + selected users within that group. */
export type TFunctionRow = {
  customGroupId: string;
  userIds: string[];
};

/** Builds a user-to-function map from function rows. */
export const buildFunctionsMap = (rows: TFunctionRow[]): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  for (const row of rows) {
    for (const userId of row.userIds) {
      if (!map[userId]) map[userId] = [];
      if (row.customGroupId && !map[userId].includes(row.customGroupId)) map[userId].push(row.customGroupId);
    }
  }
  return map;
};

/** Builds user entries from function rows - the shape expected by the API. */
export const buildUserEntries = (rows: TFunctionRow[]): Required<TApprovalUserEntry>[] => {
  const map: Record<string, string[]> = {};
  for (const row of rows) {
    for (const userId of row.userIds) {
      if (!map[userId]) map[userId] = [];
      if (!map[userId].includes(row.customGroupId)) map[userId].push(row.customGroupId);
    }
  }
  return Object.entries(map).map(([userId, customGroupIds]) => ({ userId, customGroupIds }));
};

/** Converts plain user IDs to user entries (feature-OFF path). */
export const userIdsToEntries = (userIds: string[]): TApprovalUserEntry[] => userIds.map((userId) => ({ userId }));

/** Serializes approval users according to the organization's approval-functions mode. */
export const serializeApprovalUserEntries = (
  functionsEnabled: boolean | undefined,
  rows: TFunctionRow[],
  userIds?: string[]
): TApprovalUserEntry[] => (functionsEnabled ? buildUserEntries(rows) : userIdsToEntries(userIds ?? []));

type TApprovalFunctionUserMap = Readonly<Record<string, Pick<TUser, 'name' | 'customGroups'>>>;

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/approvals/src/utils/resolveCustomGroupIds.ts
/** User IDs whose function assignment would be rejected by the approval API. */
export const usersWithInvalidFunction = (
  rows: TFunctionRow[],
  activeGroupIds: ReadonlySet<string>,
  userMap: TApprovalFunctionUserMap,
  excludedUserIds?: ReadonlySet<string>
): string[] =>
  rows.flatMap((row) =>
    row.userIds.filter(
      (userId) =>
        !excludedUserIds?.has(userId) &&
        (!row.customGroupId ||
          !activeGroupIds.has(row.customGroupId) ||
          !userMap[userId]?.customGroups.includes(row.customGroupId))
    )
  );

/** Names of users the approval API would reject, across the phases a dialog submits. */
export const resolveInvalidFunctionNames = (
  phases: { rows: TFunctionRow[]; excludedUserIds?: ReadonlySet<string> }[],
  activeGroupIds: ReadonlySet<string>,
  userMap: TApprovalFunctionUserMap
): string[] => {
  const ids = phases.flatMap(({ rows, excludedUserIds }) =>
    usersWithInvalidFunction(rows, activeGroupIds, userMap, excludedUserIds)
  );
  return Array.from(new Set(ids), (id) => userMap[id]?.name || 'Unknown');
};

/**
 * Reconstructs function rows from stored approval user entries (for pre-filling and display).
 * Users with empty customGroupIds are placed in a fallback row with customGroupId: ''
 * so they remain visible and are not silently dropped from the form.
 */
export const rowsFromApprovalUsers = (users: TApprovalUser[]): TFunctionRow[] => {
  const groupMap = new Map<string, string[]>();
  for (const u of users) {
    const ids = u.customGroupIds;
    if (ids.length === 0) {
      const arr = groupMap.get('') || [];
      if (!arr.includes(u.user)) arr.push(u.user);
      groupMap.set('', arr);
      continue;
    }
    for (const gid of ids) {
      const arr = groupMap.get(gid) || [];
      if (!arr.includes(u.user)) arr.push(u.user);
      groupMap.set(gid, arr);
    }
  }
  return Array.from(groupMap.entries()).map(([customGroupId, userIds]) => ({ customGroupId, userIds }));
};

/** Builds function rows from imported initial approval users and preserves group-only rows. */
export const rowsFromInitialApprovalUsers = (
  users: {
    user?: string;
    customGroupIds?: string[];
  }[]
): TFunctionRow[] => {
  const groupMap = new Map<string, string[]>();
  for (const u of users) {
    const ids = u.customGroupIds?.length ? [...new Set(u.customGroupIds)] : [''];
    for (const gid of ids) {
      const arr = groupMap.get(gid) || [];
      if (u.user && !arr.includes(u.user)) arr.push(u.user);
      groupMap.set(gid, arr);
    }
  }
  return Array.from(groupMap.entries()).map(([customGroupId, userIds]) => ({ customGroupId, userIds }));
};

/**
 * Builds function rows from user IDs using their current custom group assignments.
 * Each user is added to all their distinct active groups; users in more than maxActiveGroupsPerUser groups are omitted.
 * Users with no active groups are placed in a fallback row with customGroupId: ''.
 */
export const rowsFromUserIds = (
  userIds: string[],
  userMap: Record<string, { customGroups?: string[] }>,
  activeGroupIds: Set<string>,
  maxActiveGroupsPerUser: number
): TFunctionRow[] => {
  const groupMap = new Map<string, string[]>();
  for (const userId of userIds) {
    const activeUserGroupIds = new Set(
      userMap[userId]?.customGroups?.filter((groupId) => activeGroupIds.has(groupId)) ?? []
    );
    if (activeUserGroupIds.size > maxActiveGroupsPerUser) continue;
    if (activeUserGroupIds.size === 0) {
      const arr = groupMap.get('') || [];
      arr.push(userId);
      groupMap.set('', arr);
      continue;
    }
    for (const groupId of activeUserGroupIds) {
      const arr = groupMap.get(groupId) || [];
      arr.push(userId);
      groupMap.set(groupId, arr);
    }
  }
  return Array.from(groupMap.entries()).map(([customGroupId, userIds]) => ({ customGroupId, userIds }));
};
